import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import {
  CalendarCheck, CalendarX2, AlertTriangle, Clock, CheckCircle2,
  Plus, Upload, ClipboardList, FileEdit, TrendingUp, BedDouble,
  ArrowRight, Sparkles, ListChecks, Download,
} from "lucide-react";
import ReservationMethodChooser from "./ReservationMethodChooser";

const CARDS = [
  { key: "today_checkin",  title: "Bugün Girişler",     icon: CalendarCheck, from: "from-emerald-400", to: "to-emerald-600", link: "/acente/bugun-girisler" },
  { key: "today_checkout", title: "Bugün Çıkışlar",     icon: CalendarX2,    from: "from-rose-400",    to: "to-rose-600",    link: "/acente/rezervasyonlar" },
  { key: "pending",        title: "Onay Bekleyen",      icon: Clock,         from: "from-amber-400",   to: "to-amber-600",   link: "/acente/rezervasyonlar?durum=onay_bekleyen" },
  { key: "missing_info",   title: "Eksik Bilgi",        icon: AlertTriangle, from: "from-orange-400",  to: "to-orange-600",  link: "/acente/rezervasyonlar?durum=eksik_bilgi" },
  { key: "approved",       title: "Onaylı",             icon: CheckCircle2,  from: "from-brand-navy",  to: "to-brand-navy",  link: "/acente/rezervasyonlar?durum=onaylandi" },
  { key: "total",          title: "Toplam Rezervasyon", icon: ClipboardList, from: "from-brand-orange", to: "to-brand-orange-dark", link: "/acente/rezervasyonlar" },
];

const STATUS_TINT = {
  taslak: "bg-slate-100 text-slate-600",
  eksik_bilgi: "bg-orange-100 text-orange-700",
  incelemede: "bg-amber-100 text-amber-700",
  onaylandi: "bg-emerald-100 text-emerald-700",
  iptal: "bg-rose-100 text-rose-700",
};
const STATUS_LABEL = {
  taslak: "Taslak",
  eksik_bilgi: "Eksik",
  incelemede: "İncelemede",
  onaylandi: "Onaylı",
  iptal: "İptal",
};

// Format a YYYY-MM-DD date as dd.mm and label the day (Bugün/Yarın/xx gün).
function daysFromToday(iso) {
  if (!iso) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(iso + "T00:00");
  return Math.round((d - today) / (86400 * 1000));
}
function formatCheckin(iso) {
  const d = daysFromToday(iso);
  if (d === 0) return "Bugün";
  if (d === 1) return "Yarın";
  if (d > 1 && d < 30) return `${d} gün sonra`;
  if (d < 0) return "Geçmiş";
  return formatTr(iso);
}

export default function AcenteDashboard() {
  const { user } = useAcenteAuth();
  const [s, setS] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [rev, setRev] = useState(0);
  const [chooserOpen, setChooserOpen] = useState(false);

  const isAcenteRole = user?.role === "acente";
  const isOzelAcenteRole = user?.role === "ozel_acente";

  useEffect(() => {
    panelApi.get("/reports/summary").then((r) => setS(r.data)).catch(() => {});
    panelApi
      .get("/reservations")
      .then((r) => {
        // Next 5 upcoming reservations (checkin >= today)
        const today = new Date().toISOString().slice(0, 10);
        const list = (r.data || [])
          .filter((x) => x.checkin >= today && x.status !== "iptal")
          .sort((a, b) => a.checkin.localeCompare(b.checkin))
          .slice(0, 5);
        setUpcoming(list);
      })
      .catch(() => {});
    panelApi
      .get("/revisions", { params: { status: "beklemede" } })
      .then((r) => setRev(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => {});
  }, []);

  const canBook = ["acente", "ozel_acente", "admin", "operasyon", "rezervasyon"].includes(user?.role);

  // Özel acente (B2C, individual sales) — single big CTA that goes DIRECTLY
  // to the single-page rapid form. No method chooser (there's no wizard
  // alternative for them) and no group-tools shortcuts.
  if (isOzelAcenteRole) {
    return (
      <div className="space-y-6" data-testid="ozel-acente-dashboard">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-brand-navy text-white p-8 sm:p-12 shadow-xl relative overflow-hidden text-center">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.4em] text-brand-gold font-semibold">Özel Acente Paneli · Bireysel Satış</div>
            <h1 className="font-serif text-3xl sm:text-5xl mt-3">Hoş geldiniz, {user?.name}</h1>
            <p className="text-sm sm:text-base text-white/85 mt-3 max-w-lg mx-auto">
              Bugün yeni bir bireysel müşteri için rezervasyon oluşturmaya hazır mısınız?
              Tek sayfada, hızlıca tamamlayabilirsiniz.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/acente/rezervasyon/yeni"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-emerald-700 hover:bg-brand-gold hover:text-brand-navy px-7 sm:px-9 py-4 font-semibold text-lg shadow-2xl transition-all hover:-translate-y-0.5"
                data-testid="ozel-acente-primary-cta"
              >
                <Plus size={22} />
                Hızlı Rezervasyon
                <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
              </Link>
            </div>
          </div>
        </div>

        {/* Compact secondary shortcut — my reservations + monthly Excel */}
        <div className="grid gap-3 sm:grid-cols-3" data-testid="ozel-acente-quick-shortcuts">
          <Link
            to="/acente/rezervasyonlar"
            className="rounded-2xl bg-white border border-brand-navy/10 hover:border-brand-navy/30 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="ozel-shortcut-my-reservations"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-navy/10 text-brand-navy flex items-center justify-center flex-shrink-0"><ClipboardList size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-navy/60">Listem</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Rezervasyonlarım</div>
            </div>
          </Link>
          <Link
            to="/acente/rezervasyonlar?export=1"
            className="rounded-2xl bg-white border border-emerald-200 hover:border-emerald-400 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="ozel-shortcut-monthly-excel"
          >
            <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"><Download size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Muhasebe</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Aylık Excel</div>
            </div>
          </Link>
          <Link
            to="/acente/fatura-talepleri"
            className="rounded-2xl bg-white border border-brand-orange/20 hover:border-brand-orange/50 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="ozel-shortcut-invoices"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange-dark flex items-center justify-center flex-shrink-0"><ListChecks size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-orange">Fatura</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Fatura Talepleri</div>
            </div>
          </Link>
        </div>

        {/* Upcoming list — same as acente, useful context */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm overflow-hidden" data-testid="ozel-dashboard-upcoming">
          <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">Yaklaşan</div>
              <h2 className="font-serif text-xl text-brand-navy">Sıradaki Girişler</h2>
            </div>
            <Link to="/acente/rezervasyonlar" className="text-xs text-brand-orange hover:text-brand-orange-dark inline-flex items-center gap-1" data-testid="ozel-dashboard-upcoming-all">
              Tümünü Gör <ArrowRight size={12} />
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              Henüz yaklaşan rezervasyon yok. Yukarıdaki butona tıklayarak yeni bir müşteri ekleyin.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {upcoming.map((r) => {
                const rt = (r.rooms?.r1 || 0) + (r.rooms?.r2 || 0) + (r.rooms?.r3 || 0) + (r.rooms?.r4 || 0) + (r.rooms?.r5 || 0);
                const heads = (r.adults || 0) + (r.children || 0) + (r.staff || 0);
                return (
                  <li key={r.id}>
                    <Link to={`/acente/rezervasyon/${r.id}`} className="flex items-center gap-4 p-4 hover:bg-emerald-50 transition" data-testid={`ozel-dashboard-upcoming-item-${r.id}`}>
                      <div className="min-w-[80px] text-center bg-canvas-cream rounded-xl px-3 py-2 border border-brand-navy/10">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{formatCheckin(r.checkin)}</div>
                        <div className="font-mono text-sm text-brand-navy mt-1">{formatTr(r.checkin)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-brand-navy truncate">{r.group_name || r.customer_phone || "—"}</span>
                          <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_TINT[r.status] || "bg-slate-100"}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-mono">{r.reservation_number}</span>
                          {r.customer_phone && <span>📞 {r.customer_phone}</span>}
                          <span>{r.nights} gece</span>
                          <span>{heads} kişi</span>
                          <span className="inline-flex items-center gap-1"><BedDouble size={11}/> {rt} oda</span>
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // Simplified experience for the "acente" role — a single big CTA that
  // opens the ReservationMethodChooser. Staff roles still see the KPI grid.
  if (isAcenteRole) {
    return (
      <div className="space-y-6" data-testid="acente-dashboard-simple">
        {/* Big centered hero — single CTA, no cognitive overload */}
        <div className="rounded-3xl bg-gradient-to-br from-brand-orange via-brand-orange-dark to-brand-navy text-white p-8 sm:p-12 shadow-xl relative overflow-hidden text-center">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.4em] text-brand-gold font-semibold">Ariaf ASIS Acente Paneli</div>
            <h1 className="font-serif text-3xl sm:text-5xl mt-3">Hoş geldiniz, {user?.name}</h1>
            <p className="text-sm sm:text-base text-white/85 mt-3 max-w-lg mx-auto">
              Bugün yeni bir grup için rezervasyon oluşturmaya hazır mısınız?
              Aşağıdaki butona tıklayın — size iki hızlı yol sunacağız.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setChooserOpen(true)}
                className="group inline-flex items-center gap-3 rounded-full bg-white text-brand-orange hover:bg-brand-gold hover:text-brand-navy px-7 sm:px-9 py-4 font-semibold text-lg shadow-2xl transition-all hover:-translate-y-0.5"
                data-testid="acente-primary-cta"
              >
                <Plus size={22} />
                Rezervasyon Oluştur
                <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
              </button>
            </div>
            {rev > 0 && (
              <Link
                to="/acente/revizyon-talepleri"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-gold/20 hover:bg-brand-gold/30 backdrop-blur text-white px-4 py-2 text-sm transition"
                data-testid="acente-revisions-cta"
              >
                <FileEdit size={14} /> {rev} bekleyen revizyon talebiniz var
              </Link>
            )}
          </div>
        </div>

        {/* Compact secondary shortcuts — kolay araçlar + rezervasyonlarım */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="acente-quick-shortcuts">
          <Link
            to="/acente/rezervasyonlar"
            className="rounded-2xl bg-white border border-brand-navy/10 hover:border-brand-navy/30 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="shortcut-my-reservations"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-navy/10 text-brand-navy flex items-center justify-center flex-shrink-0"><ClipboardList size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-navy/60">Listem</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Rezervasyonlarım</div>
            </div>
          </Link>
          <Link
            to="/acente/grup-programi"
            className="rounded-2xl bg-white border border-brand-orange/20 hover:border-brand-orange/50 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="shortcut-group-program"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange-dark flex items-center justify-center flex-shrink-0"><CalendarCheck size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-orange">Yardımcı Araç</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Grup Programı</div>
            </div>
          </Link>
          <Link
            to="/acente/oda-listesi"
            className="rounded-2xl bg-white border border-brand-orange/20 hover:border-brand-orange/50 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="shortcut-room-list"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-orange/10 text-brand-orange-dark flex items-center justify-center flex-shrink-0"><BedDouble size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-orange">Yardımcı Araç</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Oda Listesi</div>
            </div>
          </Link>
          <Link
            to="/acente/toplu-yukle"
            className="rounded-2xl bg-white border border-brand-navy/10 hover:border-brand-navy/30 hover:shadow-md p-4 flex items-center gap-3 transition"
            data-testid="shortcut-bulk-upload"
          >
            <div className="w-11 h-11 rounded-xl bg-brand-navy/10 text-brand-navy flex items-center justify-center flex-shrink-0"><Upload size={20}/></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-navy/60">Toplu</div>
              <div className="font-serif text-lg text-brand-navy leading-tight">Excel Yükle</div>
            </div>
          </Link>
        </div>

        {/* Upcoming reservations — kept, essential context */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm overflow-hidden" data-testid="dashboard-upcoming">
          <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">Yaklaşan</div>
              <h2 className="font-serif text-xl text-brand-navy">Sıradaki Girişler</h2>
            </div>
            <Link to="/acente/rezervasyonlar" className="text-xs text-brand-orange hover:text-brand-orange-dark inline-flex items-center gap-1" data-testid="dashboard-upcoming-all">
              Tümünü Gör <ArrowRight size={12} />
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              Henüz yaklaşan rezervasyon yok. Yukarıdaki butona tıklayarak yeni bir grup ekleyin.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {upcoming.map((r) => {
                const rt = (r.rooms?.r1 || 0) + (r.rooms?.r2 || 0) + (r.rooms?.r3 || 0) + (r.rooms?.r4 || 0) + (r.rooms?.r5 || 0);
                const heads = (r.adults || 0) + (r.children || 0) + (r.staff || 0);
                return (
                  <li key={r.id}>
                    <Link to={`/acente/rezervasyon/${r.id}`} className="flex items-center gap-4 p-4 hover:bg-brand-orange/5 transition" data-testid={`dashboard-upcoming-item-${r.id}`}>
                      <div className="min-w-[80px] text-center bg-canvas-cream rounded-xl px-3 py-2 border border-brand-navy/10">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{formatCheckin(r.checkin)}</div>
                        <div className="font-mono text-sm text-brand-navy mt-1">{formatTr(r.checkin)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-brand-navy truncate">{r.group_name}</span>
                          <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_TINT[r.status] || "bg-slate-100"}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-mono">{r.reservation_number}</span>
                          <span>{r.nights} gece</span>
                          <span>{heads} kişi</span>
                          <span className="inline-flex items-center gap-1"><BedDouble size={11}/> {rt} oda</span>
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <ReservationMethodChooser open={chooserOpen} onOpenChange={setChooserOpen} />
      </div>
    );
  }

  // -------- Staff (admin / operasyon) full KPI dashboard follows --------

  return (
    <div className="space-y-6" data-testid="acente-dashboard">
      <div className="rounded-3xl bg-gradient-to-br from-brand-orange via-brand-orange-dark to-brand-navy text-white p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.4em] text-brand-gold font-semibold">Panel</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2">Hoş geldiniz, {user?.name}</h1>
          <p className="text-sm text-white/85 mt-2 max-w-xl">Rezervasyon durumunuza tek bakışta göz atın. Yeni bir grup için hızlı ekleme veya Excel'den toplu içe aktarma yapabilirsiniz.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {canBook && (
              <>
                <Link
                  to="/acente/rezervasyon/yeni"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-brand-orange hover:bg-brand-gold hover:text-brand-navy px-5 py-2.5 font-medium shadow-lg transition"
                  data-testid="acente-new-reservation-cta"
                >
                  <Plus size={16} /> Rezervasyon Oluştur
                </Link>
                <Link
                  to="/acente/toplu-yukle"
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white px-5 py-2.5 font-medium transition"
                  data-testid="acente-bulk-cta"
                >
                  <Upload size={16} /> Excel'den Toplu Yükle
                </Link>
              </>
            )}
            <Link
              to="/acente/analitik"
              className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white px-5 py-2.5 font-medium transition"
              data-testid="acente-analytics-cta"
            >
              <TrendingUp size={16} /> Analitik
            </Link>
            {rev > 0 && (
              <Link
                to="/acente/revizyon-talepleri"
                className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy hover:brightness-95 px-5 py-2.5 font-medium shadow transition"
                data-testid="acente-revisions-cta"
              >
                <FileEdit size={16} /> {rev} bekleyen revizyon
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3" data-testid="acente-dashboard-cards">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.key}
              to={c.link}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${c.from} ${c.to} text-white p-5 shadow-md transition hover:-translate-y-1 hover:shadow-lg block`}
              data-testid={`dashboard-card-${c.key}`}
            >
              <div className="absolute -bottom-4 -right-4 opacity-20">
                <Icon size={80} />
              </div>
              <div className="relative">
                <div className="w-9 h-9 rounded-xl bg-white/25 backdrop-blur flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <div className="mt-3 font-serif text-3xl leading-none">{s ? s[c.key] : "…"}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/85 mt-2">{c.title}</div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Upcoming reservations list — "useful" quick view */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm overflow-hidden" data-testid="dashboard-upcoming">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">Yaklaşan</div>
            <h2 className="font-serif text-xl text-brand-navy">Sıradaki Girişler</h2>
          </div>
          <Link
            to="/acente/rezervasyonlar"
            className="text-xs text-brand-orange hover:text-brand-orange-dark inline-flex items-center gap-1"
            data-testid="dashboard-upcoming-all"
          >
            Tümünü Gör <ArrowRight size={12} />
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Yaklaşan rezervasyon yok.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {upcoming.map((r) => {
              const rt = (r.rooms?.r1 || 0) + (r.rooms?.r2 || 0) + (r.rooms?.r3 || 0) + (r.rooms?.r4 || 0) + (r.rooms?.r5 || 0);
              const heads = (r.adults || 0) + (r.children || 0) + (r.staff || 0);
              return (
                <li key={r.id}>
                  <Link
                    to={`/acente/rezervasyon/${r.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-brand-orange/5 transition"
                    data-testid={`dashboard-upcoming-item-${r.id}`}
                  >
                    <div className="min-w-[80px] text-center bg-canvas-cream rounded-xl px-3 py-2 border border-brand-navy/10">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{formatCheckin(r.checkin)}</div>
                      <div className="font-mono text-sm text-brand-navy mt-1">{formatTr(r.checkin)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-brand-navy truncate">{r.group_name}</span>
                        <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_TINT[r.status] || "bg-slate-100"}`}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="font-mono">{r.reservation_number}</span>
                        <span>{r.nights} gece</span>
                        <span>{heads} kişi</span>
                        <span className="inline-flex items-center gap-1"><BedDouble size={11}/> {rt} oda</span>
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-slate-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
